import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { PageHeader } from '../../shared/components/PageHeader';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { StatusChip } from '../../shared/components/StatusChip';
import { DetailsInfoTooltip } from '../../shared/components/DetailsInfoTooltip';
import { AddPromptPanel } from '../../shared/components/AddPromptPanel';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import { formatDate } from '../../shared/utils/format';
import type { Prompt, PromptSetPrompt } from '../../core/types/data-models';

/** Junction row with the related prompt eagerly included by Prisma. */
type AttachedPrompt = PromptSetPrompt & { prompt?: Prompt };

export function PromptSetDetail() {
  const { id } = useParams();
  const promptSetId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const junction = useMemo(() => resources.promptSetPromptsFor(promptSetId), [promptSetId]);

  const promptSet = useQuery({
    queryKey: ['prompt-sets', promptSetId],
    queryFn: () => resources.promptSets.get(promptSetId),
    enabled: Number.isFinite(promptSetId),
  });

  const attached = useQuery({
    queryKey: ['prompt-set-prompts', promptSetId],
    queryFn: () => junction.list({ pageSize: 200, include: ['prompt'] }),
    enabled: Number.isFinite(promptSetId),
  });

  // Lookups for the attached-prompts table — share cache keys with the
  // catalogs `AddPromptPanel` uses internally. The default category /
  // persona / brand FKs on Prompt now resolve against the LOB-/tenant-
  // scoped tables.
  const categories = useQuery({
    queryKey: ['line-of-biz-categories', 'select'],
    queryFn: () =>
      resources.lineOfBizCategories.list({ pageSize: 200, sort: 'name' }),
  });
  const personas = useQuery({
    queryKey: ['line-of-biz-personas', 'select'],
    queryFn: () =>
      resources.lineOfBizPersonas.list({ pageSize: 200, sort: 'name' }),
  });
  const stages = useQuery({
    queryKey: ['prompt-funnel-stages', 'select'],
    queryFn: () => resources.promptFunnelStages.list({ pageSize: 200, sort: 'name' }),
  });
  const brands = useQuery({
    queryKey: ['tenant-brands', 'select', 'all'],
    queryFn: () => resources.tenantBrands.list({ pageSize: 200, sort: 'name' }),
  });

  const attachedRows = (attached.data?.data as AttachedPrompt[] | undefined) ?? [];
  const attachedIds = useMemo(
    () => new Set(attachedRows.map((r) => r.promptId)),
    [attachedRows],
  );

  const attach = useMutation({
    // PUT /prompt-sets/:promptSetId/prompts/:promptId is the idempotent
    // attach on the Junction service.
    mutationFn: (promptId: number) => junction.update(promptId, {}),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['prompt-set-prompts', promptSetId] });
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const detach = useMutation({
    mutationFn: (promptId: number) => junction.remove(promptId),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['prompt-set-prompts', promptSetId] });
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  // Resolve FK ids to display names from the shared caches above so the
  // table reads the same as the global Prompts view.
  const nameById = (rows?: { id: number; name: string }[]) => {
    const m = new Map<number, string>();
    for (const r of rows ?? []) m.set(r.id, r.name);
    return m;
  };
  const categoryName = useMemo(() => nameById(categories.data?.data), [categories.data]);
  const personaName = useMemo(() => nameById(personas.data?.data), [personas.data]);
  const stageName = useMemo(() => nameById(stages.data?.data), [stages.data]);
  const brandName = useMemo(() => nameById(brands.data?.data), [brands.data]);

  const columns: GridColDef[] = useMemo(() => {
    const lookupCell =
      (map: Map<number, string>) =>
      (p: GridRenderCellParams) => {
        const id = (p.row as AttachedPrompt).prompt?.[p.field as keyof Prompt] as
          | number
          | null
          | undefined;
        if (id == null) return '';
        return map.get(id as number) ?? `#${id}`;
      };
    return [
      // Match the Prompts page columns 1:1 so the two tables read the same.
      { field: 'promptId', headerName: 'ID', width: 70 },
      {
        field: 'prompt',
        headerName: 'Prompt text',
        flex: 1,
        minWidth: 320,
        valueGetter: (_v, row: AttachedPrompt) => row.prompt?.prompt ?? '',
      },
      {
        field: 'language',
        headerName: 'Lang',
        width: 80,
        valueGetter: (_v, row: AttachedPrompt) => row.prompt?.language ?? '',
      },
      {
        field: 'defaultCategoryId',
        headerName: 'Default Category',
        width: 180,
        renderCell: lookupCell(categoryName),
      },
      {
        field: 'defaultPersonaId',
        headerName: 'Default Persona',
        width: 180,
        renderCell: lookupCell(personaName),
      },
      {
        field: 'defaultStageId',
        headerName: 'Default Stage',
        width: 180,
        renderCell: lookupCell(stageName),
      },
      {
        field: 'defaultBrandId',
        headerName: 'Default Brand',
        width: 200,
        renderCell: lookupCell(brandName),
      },
      {
        field: 'defaultImportance',
        headerName: 'Importance',
        width: 130,
        renderCell: (p: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <StatusChip value={(p.row as AttachedPrompt).prompt?.defaultImportance} />
          </Box>
        ),
      },
      {
        field: '__detach',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 70,
        align: 'right',
        headerAlign: 'right',
        renderCell: (p: GridRenderCellParams) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              height: '100%',
            }}
          >
            <IconButton
              size="small"
              color="error"
              onClick={() => detach.mutate((p.row as AttachedPrompt).promptId)}
              disabled={detach.isPending}
              aria-label="detach prompt"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ];
  }, [categoryName, personaName, stageName, brandName, detach]);

  return (
    // Flex column so the attached-prompts table can flex into the
    // remaining viewport height under the page header + collapsible
    // Add-prompt accordion.
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={600}>
              {promptSet.data?.name ?? `Prompt set #${promptSetId}`}
            </Typography>
            <DetailsInfoTooltip
              rows={[
                { label: 'ID', value: promptSet.data?.id ?? '—' },
                { label: 'Created', value: formatDate(promptSet.data?.createdAt) },
                { label: 'Updated', value: formatDate(promptSet.data?.updatedAt) },
                { label: 'Prompts attached', value: attachedRows.length },
              ]}
            />
          </Stack>
        }
        description={promptSet.data?.description ?? undefined}
        actions={<Button onClick={() => navigate(-1)}>Back</Button>}
      />

      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <AddPromptPanel
          addButtonLabel="Add to set"
          excludePromptIds={attachedIds}
          error={error}
          onErrorClose={() => setError(null)}
          isAdding={attach.isPending}
          onAdd={async (prompt) => {
            await attach.mutateAsync(prompt.id);
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Attached prompts
          </Typography>
          <DataTable<AttachedPrompt>
            rows={attachedRows}
            columns={columns}
            loading={attached.isFetching}
            paginationMode="client"
            emptyMessage="No prompts attached yet. Use the picker above to add some."
            errorMessage={attached.isError ? extractErrorMessage(attached.error) : undefined}
          />
        </Box>
      </Stack>
    </Box>
  );
}

